// src/pages/Checkout.js
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { gradients } from "./theme/theme";
import { db } from "./firebase/firebaseConfig";
import { doc, getDoc } from "firebase/firestore";
import { FaCheck } from "react-icons/fa";

const Checkout = () => {
  const { id } = useParams();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch course details
  useEffect(() => {
    const fetchCourse = async () => {
      try {
        const courseRef = doc(db, "courses", id);
        const snap = await getDoc(courseRef);
        if (snap.exists()) {
          setCourse({ id: snap.id, ...snap.data() });
        } else {
          console.error("❌ Course not found");
        }
      } catch (err) {
        console.error("Error fetching course:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCourse();
  }, [id]);

  if (loading) {
    return (
      <Container>
        <h2 style={{ color: "#fff" }}>Loading course details...</h2>
      </Container>
    );
  }

  if (!course) {
    return (
      <Container>
        <h2 style={{ color: "red" }}>❌ Course not found</h2>
      </Container>
    );
  }

  // ✅ Calculate discounted price
  const discountedPrice =
    course.discount > 0
      ? (course.price - (course.price * course.discount) / 100).toFixed(2)
      : course.price;

  return (
    <Container>
      {/* Left Section */}
      <Left>
        <CoursePreview>
          <img
            src={course.imageUrl || "https://via.placeholder.com/600x300"}
            alt={course.title}
          />
          <h2>{course.title}</h2>

          <h3>Course Chapters</h3>
          <ChapterList>
            {course.chapters && course.chapters.length > 0 ? (
              course.chapters.map((chap, index) => (
                <ChapterItem key={index}>
                  <FaCheck /> {chap.title}{" "}
                  {chap.duration ? `(${chap.duration})` : ""}
                </ChapterItem>
              ))
            ) : (
              <p>No chapters available</p>
            )}
          </ChapterList>
        </CoursePreview>
      </Left>

      {/* Right Section */}
      <Right>
        <CheckoutCard>
          {/* Price with discount */}
          <PriceRow>
            <Price>{course.price === 0 ? "Free" : `₹${discountedPrice}`}</Price>
            {course.discount > 0 && (
              <>
                <OldPrice>₹{course.price}</OldPrice>
                <Discount>{course.discount}% OFF</Discount>
              </>
            )}
          </PriceRow>

          <CouponBox>
            <input type="text" placeholder="Enter coupon" />
            <button>Apply</button>
          </CouponBox>

          <CheckoutButton>
            {course.price === 0 ? "Start Course" : "Proceed to Payment"}
          </CheckoutButton>
        </CheckoutCard>
      </Right>
    </Container>
  );
};

export default Checkout;

// ---------------- styled-components ----------------

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 2rem;
  padding: 2rem;
  background: ${gradients.heroBackground};
  min-height: 100vh;
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Left = styled.div`
  flex: 2;
`;

const CoursePreview = styled.div`
  background: #1e1e2f;
  padding: 1.5rem;
  border-radius: 16px;
  box-shadow: 0px 6px 16px rgba(0, 0, 0, 0.15);

  img {
    width: 100%;
    //height: 350px;
    object-fit: cover;
    border-radius: 12px;
    margin-bottom: 1rem;
  }

  h2 {
    font-size: 2rem;
    font-weight: bold;
    margin-bottom: 1.2rem;
    background: linear-gradient(135deg, #ff7a18, #ff0066);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  h3 {
    margin-top: 1.2rem;
    margin-bottom: 0.8rem;
    color: #00d4ff;
  }
`;

const ChapterList = styled.div`
  margin-top: 10px;
`;

const ChapterItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  color: #ddd;
  svg {
    color: #00d4ff;
    margin-right: 8px;
  }
`;

const Right = styled.div`
  flex: 1;
`;

const CheckoutCard = styled.div`
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(8px);
  padding: 2rem;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

// ✅ Price Row
const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 15px;
  margin: 1.2rem 0;
`;

const Price = styled.div`
  font-size: 2.4rem;
  font-weight: 700;
  color: #3cff0b;
`;

const OldPrice = styled.div`
  font-size: 1.2rem;
  text-decoration: line-through;
  color: #aaa;
`;

const Discount = styled.div`
  font-size: 0.9rem;
  background: #ff1744;
  color: #fff;
  padding: 4px 10px;
  border-radius: 6px;
  font-weight: 600;
`;

const CouponBox = styled.div`
  display: flex;
  width: 100%;
  margin: 1rem 0;

  input {
    flex: 1;
    padding: 0.7rem;
    border: 1px solid #ccc;
    border-radius: 8px 0 0 8px;
    outline: none;
  }

  button {
    padding: 0.7rem 1rem;
    border: none;
    background: #6c63ff;
    color: #fff;
    font-weight: 600;
    cursor: pointer;
    border-radius: 0 8px 8px 0;
    transition: 0.3s;

    &:hover {
      background: #5146d9;
    }
  }
`;

const CheckoutButton = styled.button`
  width: 100%;
  padding: 1rem;
  border: none;
  background: linear-gradient(135deg, #ff7a18, #ff0066);
  color: #fff;
  font-size: 1.2rem;
  border-radius: 12px;
  cursor: pointer;
  margin-top: 1rem;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    background: linear-gradient(135deg, #ff5722, #e91e63);
  }
`;
